import client from './client';
import { pollVistaJob } from './vistaBatchExport';
import { JobStartResponse, JobStatus, JobStatusEnum } from '../interfaces/V2Interface';

const startVistaPostVerificationReportJob = async (
  payPeriodID: number | string
): Promise<JobStartResponse> => {
  const response = await client.post('/admin/riceServices/reports/vistaPostVerificationReportJob', {
    payPeriodID,
  });
  return response.data as JobStartResponse;
};

const downloadVistaPostVerificationReportJob = async (
  jobID: number
): Promise<{ blob: Blob; fileName: string | null }> => {
  const response = await client.get(
    `/admin/riceServices/reports/vistaPostVerificationReportJob/${jobID}/download`,
    { responseType: 'blob' }
  );
  const disposition: string | undefined = response.headers['content-disposition'];
  let fileName: string | null = null;
  if (disposition) {
    const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'";\n]+)\1/);
    if (match) fileName = match[2].trim();
  }
  return { blob: response.data as Blob, fileName };
};

/**
 * Job-based flow: start the VISTA_POST_VERIFICATION_REPORT job, poll to a terminal state, then
 * download the xlsx.
 *
 * Uses `pollVistaJob` rather than the sibling Payroll Summary client's bare `while (true)` loop:
 * this job reads two slow timesheet passes plus two Vista queries and runs for minutes, so the
 * poller has to be abortable or it outlives the page.
 */
export const downloadVistaPostVerificationReport = async (
  payPeriodID: number | string,
  fallbackFileName?: string,
  onProgress?: (jobStatus: JobStatus) => void,
  signal?: AbortSignal
): Promise<void> => {
  const { jobID } = await startVistaPostVerificationReportJob(payPeriodID);

  const finalStatus = await pollVistaJob(jobID, onProgress, signal);

  if (finalStatus.status === JobStatusEnum.FAILED) {
    const resultData = finalStatus.resultData as { error?: string; message?: string } | undefined;
    throw new Error(
      resultData?.error || resultData?.message || 'Vista post verification report generation failed.'
    );
  }

  const { blob, fileName } = await downloadVistaPostVerificationReportJob(jobID);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName ?? fallbackFileName ?? `VistaPostVerification_${payPeriodID}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
